'use client';
import {usePathname, useRouter} from 'next/navigation';
import {trackLanguageSwitch} from '../../analytics';
import {localeHref, splitLocalePathname} from '../../i18n';
import {LOCALE_LABELS, type Locale} from '../../shared/constants/locales';
import {Button} from '../primitives';

interface LocaleSwitcherProps {
  current: Locale;
  // Which locales are actually routable. Must come from a server component: the
  // browser bundle cannot read the DISABLED_LOCALES runtime env var.
  locales: readonly Locale[];
}

export function LocaleSwitcher({current, locales}: LocaleSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();

  const target = locales.find(locale => locale !== current);
  const switchLocale = () => {
    if (!target) {
      return;
    }
    trackLanguageSwitch(target, current);
    const newPath = localeHref(target, splitLocalePathname(pathname).path);
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; secure' : '';
    document.cookie = `NEXT_LOCALE=${target}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax${secure}`;
    router.push(newPath);
  };

  if (!target) {
    return null;
  }

  return (
    <Button
      variant="link"
      onClick={switchLocale}
      labelProps={{
        size: 'body-1',
        weight: 'medium',
        mobileSize: 'body-2',
        mobileWeight: 'semiBold',
        // The theme family follows the current direction (Tajawal under RTL); the
        // switcher label always renders in the Latin face, so override it.
        sx: theme => ({
          fontFamily: theme.typography.fontFamilyLatin,
          [theme.breakpoints.down('md')]: {fontFamily: theme.typography.fontFamilyLatin},
        }),
      }}
      sx={theme => ({
        color: theme.palette.colors.text.neutral.inverse,
        padding: 0,
        minWidth: 0,
      })}
    >
      {LOCALE_LABELS[target]}
    </Button>
  );
}

LocaleSwitcher.displayName = 'LocaleSwitcher';
